'use client';

import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import Map, { Marker, Popup, NavigationControl, MapRef } from 'react-map-gl/mapbox';
import mapboxgl from 'mapbox-gl';
import './DirectoryMapMini.css';
import { MapPin, Star, ArrowRight, Maximize2 } from 'lucide-react';
import Link from 'next/link';
import { AnimatePresence, motion } from 'framer-motion';
import { LANDING_COLORS } from '@/constants/landing-colors';
import { MAPBOX_TOKEN, useMapboxCss } from '@/lib/mapbox';
import { TYPE_COLORS, TYPE_ICONS, TYPE_LABELS, type DirectoryType } from '@/constants/directoryTypes';

interface Listing {
  id: string;
  name: string;
  slug: string;
  type: DirectoryType;
  city: string;
  latitude?: number | null;
  longitude?: number | null;
  rating?: number | null;
  review_count?: number;
  is_promoted?: boolean;
  promotion_order?: number;
}

interface DirectoryMapMiniProps {
  listings: Listing[];
  onExpand: () => void;
  selectedCity?: string;
  customCenter?: { latitude: number; longitude: number; zoom?: number };
  /** Modo vista previa estática (dentro de la card del grid): oculta badge/expandir/nav,
   *  el mapa sirve solo de fondo y el click lo maneja el contenedor. */
  staticPreview?: boolean;
  persistent?: boolean;
  expanded?: boolean;
}

// Spain center (Madrid)
const SPAIN_CENTER = {
  latitude: 40.4168,
  longitude: -3.7038,
  zoom: 5,
};

const colors = LANDING_COLORS.jams;

export default function DirectoryMapMini({ listings, onExpand, customCenter, staticPreview = false, persistent = false, expanded = false }: DirectoryMapMiniProps) {
  const [failed, setFailed] = useState(false);
  const mapRef = useRef<MapRef>(null);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const cssReady = useMapboxCss();
  // Filter listings that have coordinates - memoized to prevent recalculation
  const listingsWithCoords = useMemo(() =>
    listings.filter(l => typeof l.latitude === 'number' && Number.isFinite(l.latitude) && typeof l.longitude === 'number' && Number.isFinite(l.longitude)),
    [listings]
  );

  // Calculate initial view based on listings - memoized
  const initialViewState = useMemo(() => {
    if (persistent && customCenter) return { latitude: customCenter.latitude, longitude: customCenter.longitude, zoom: customCenter.zoom ?? 11 };
    if (persistent && listingsWithCoords.length) {
      const lats = listingsWithCoords.map(l => l.latitude!), lngs = listingsWithCoords.map(l => l.longitude!);
      return { latitude: (Math.min(...lats)+Math.max(...lats))/2, longitude:(Math.min(...lngs)+Math.max(...lngs))/2, zoom:11 };
    }
    const promotedListings = listingsWithCoords.filter(l => l.is_promoted);

    if (promotedListings.length > 0) {
      const lats = promotedListings.map(l => l.latitude!);
      const lngs = promotedListings.map(l => l.longitude!);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);

      const avgLat = (minLat + maxLat) / 2;
      const avgLng = (minLng + maxLng) / 2;

      const latDiff = maxLat - minLat;
      const lngDiff = maxLng - minLng;
      const maxDiff = Math.max(latDiff, lngDiff);

      let zoom = 10;
      if (maxDiff >= 0.1) zoom = 8;
      else if (maxDiff >= 0.05) zoom = 9;
      else if (maxDiff >= 0.01) zoom = 9.5;

      return { latitude: avgLat, longitude: avgLng, zoom };
    }

    if (customCenter) {
      return {
        latitude: customCenter.latitude,
        longitude: customCenter.longitude,
        zoom: customCenter.zoom || 10,
      };
    }

    if (listingsWithCoords.length === 0) return SPAIN_CENTER;

    const avgLat = listingsWithCoords.reduce((sum, l) => sum + (l.latitude || 0), 0) / listingsWithCoords.length;
    const avgLng = listingsWithCoords.reduce((sum, l) => sum + (l.longitude || 0), 0) / listingsWithCoords.length;

    return {
      latitude: avgLat,
      longitude: avgLng,
      zoom: listingsWithCoords.length === 1 ? 11 : 9,
    };
  }, [listingsWithCoords, customCenter, persistent]);

  const syncView = useCallback(() => {
    if (!persistent || !mapRef.current) return;
    if (listingsWithCoords.length && !customCenter) {
      const lats=listingsWithCoords.map(l=>l.latitude!), lngs=listingsWithCoords.map(l=>l.longitude!);
      mapRef.current.fitBounds([[Math.min(...lngs),Math.min(...lats)],[Math.max(...lngs),Math.max(...lats)]], {padding:36,maxZoom:14,duration:0});
    } else mapRef.current.jumpTo({center:[initialViewState.longitude,initialViewState.latitude],zoom:initialViewState.zoom});
  },[persistent,listingsWithCoords,customCenter,initialViewState]);
  useEffect(syncView,[syncView]);

  const handleMarkerClick = useCallback((listing: Listing) => {
    setSelectedListing(listing);

    mapRef.current?.flyTo({
      center: [listing.longitude!, listing.latitude! + (persistent ? 0 : 0.003)],
      zoom: 14,
      duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 800,
    });
  }, [persistent]);

  if (!cssReady) return (
    <div className="w-full h-full min-h-[180px] bg-gray-100 animate-pulse rounded-lg" aria-label="Cargando mapa" />
  );
  
  if (!MAPBOX_TOKEN || failed) {
    return (
      <div className="w-full h-full min-h-[180px] p-5 text-center flex flex-col gap-3 items-center justify-center bg-gray-100" role="status">
        <p className="text-gray-600 text-sm">No se puede mostrar el mapa ahora. Puedes seguir explorando las fichas.</p>
        {failed && <button className="text-sm underline min-h-11" onClick={() => setFailed(false)}>Reintentar mapa</button>}
      </div>
    );
  }

  return (
    <div
      className="relative w-full h-full overflow-hidden"
      /* En preview, `contain: paint` + isolation fuerzan el recorte de los markers de
         mapbox (position absolute + z-index altos) dentro de la card, para que no se
         escapen por encima del header sticky al hacer scroll. */
      style={staticPreview ? { contain: 'paint', isolation: 'isolate' } : undefined}
    >
      <div className={persistent ? "h-full relative" : "h-full min-h-[220px] relative"}>
        <Map
          ref={mapRef}
          onError={() => setFailed(true)}
          onLoad={syncView}
          scrollZoom={false}
          initialViewState={initialViewState}
          onClick={() => setSelectedListing(null)}
          style={{ width: '100%', height: '100%' }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          mapboxAccessToken={MAPBOX_TOKEN}
          mapLib={mapboxgl}
          /* En preview (card del grid/lista) el mapa es solo fondo: sin gestos, para que
             el tap/click lo maneje el contenedor y abra el modal. */
          interactive={!staticPreview}
        >
          {!staticPreview && <NavigationControl position="top-right" showCompass={false} />}

          {(persistent ? listingsWithCoords : listingsWithCoords.slice(0, 50)).map(listing => {
            const typeColor = TYPE_COLORS[listing.type] || colors.primary;
            const Icon = TYPE_ICONS[listing.type] || MapPin;
            const isSelected = selectedListing?.id === listing.id;

            let zIndex = 1;
            if (isSelected) {
              zIndex = 1000;
            } else if (listing.is_promoted && listing.promotion_order) {
              zIndex = 800 - (listing.promotion_order * 100);
            } else if (listing.is_promoted) {
              zIndex = 500;
            }

            return (
              <Marker
                key={listing.id}
                latitude={listing.latitude!}
                longitude={listing.longitude!}
                anchor="bottom"
                onClick={(e) => {
                  if (staticPreview) return;
                  e.originalEvent.stopPropagation();
                  handleMarkerClick(listing);
                }}
                style={{ zIndex, ...(staticPreview ? { pointerEvents: 'none' as const } : {}) }}
              >
                {persistent ? <button type="button" aria-label={`Ver en el mapa: ${listing.name}`} onClick={e => { e.stopPropagation(); handleMarkerClick(listing); }} style={{width:32,height:32,borderRadius:'50%',background:typeColor,border:'2px solid white',color:'white',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 2px 5px #17182730'}}><Icon size={16} aria-hidden /></button> : (
                <div role={staticPreview ? undefined : 'button'} tabIndex={staticPreview ? undefined : 0} aria-label={`Ver en el mapa: ${listing.name}`} onKeyDown={e => { if (!staticPreview && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); e.stopPropagation(); handleMarkerClick(listing); } }}
                  className={`relative transition-transform duration-150 ${staticPreview ? '' : 'cursor-pointer hover:scale-110 active:scale-95'}`}
                >
                  {listing.is_promoted && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-400 rounded-full flex items-center justify-center shadow-sm z-10">
                      <Star className="w-2.5 h-2.5 text-white fill-white" />
                    </div>
                  )}

                  <div
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-full shadow-lg border-2 transition-all ${
                      isSelected
                        ? 'bg-white border-gray-900 scale-110'
                        : listing.is_promoted
                          ? 'bg-amber-50 border-amber-400 hover:border-amber-500'
                          : 'bg-white border-white hover:border-gray-200'
                    }`}
                    style={{
                      boxShadow: listing.is_promoted && !isSelected
                        ? '0 4px 12px -2px rgba(245, 158, 11, 0.4)'
                        : undefined
                    }}
                  >
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: typeColor }}
                    >
                      <Icon className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-[10px] font-bold text-gray-900 max-w-[80px] truncate">
                      {listing.name}
                    </span>
                  </div>
                  <div
                    className={`w-2.5 h-2.5 rotate-45 mx-auto -mt-1 border-r-2 border-b-2 ${
                      isSelected
                        ? 'bg-white border-gray-900'
                        : listing.is_promoted
                          ? 'bg-amber-50 border-amber-400'
                          : 'bg-white border-white'
                    }`}
                  />
                </div>
                )}
              </Marker>
            );
          })}

          <AnimatePresence>
            {selectedListing && listingsWithCoords.some(l => l.id === selectedListing.id) && (
              <Popup
                className="directory-map-popup"
                latitude={selectedListing.latitude!}
                longitude={selectedListing.longitude!}
                offset={persistent ? 36 : 25}
                closeButton={false}
                closeOnClick={false}
                anchor="bottom"
                // Selected markers use layer 1000; the summary must cover them.
                style={{ zIndex: 1001 }}
                maxWidth="220px"
              >
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 5 }}
                >
                  <Link
                    href={`/directorio/${selectedListing.slug}`}
                    className="block bg-white rounded-xl overflow-hidden shadow-lg border border-gray-100 hover:border-gray-200 transition-colors"
                  >
                    <div className="p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className="text-[9px] font-semibold px-1.5 py-0.5 rounded"
                          style={{
                            backgroundColor: `${TYPE_COLORS[selectedListing.type]}15`,
                            color: TYPE_COLORS[selectedListing.type]
                          }}
                        >
                          {TYPE_LABELS[selectedListing.type]}
                        </span>
                        {selectedListing.rating && (
                          <span className="flex items-center gap-0.5 text-[10px]">
                            <Star className="w-2.5 h-2.5 text-amber-400 fill-current" />
                            <span className="font-bold">{selectedListing.rating}</span>
                          </span>
                        )}
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm truncate mb-1">
                        {selectedListing.name}
                      </h4>
                      <p className="text-[10px] text-gray-500 flex items-center gap-1 mb-2">
                        <MapPin className="w-2.5 h-2.5" />
                        {selectedListing.city}
                      </p>
                      <span
                        className="inline-flex items-center gap-1 text-xs font-semibold"
                        style={{ color: colors.primary }}
                      >
                        Ver perfil
                        <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              </Popup>
            )}
          </AnimatePresence>
        </Map>

        {!staticPreview && (
          <div
            className="absolute top-3 left-3 rounded-full px-3 py-1.5 shadow-md"
            style={{ backgroundColor: persistent ? 'var(--color-brand)' : colors.primary }}
          >
            <span className="text-xs font-bold text-white">
              {listingsWithCoords.length} en el mapa
            </span>
          </div>
        )}

        {!staticPreview && (
          <button
            onClick={onExpand}
            className="absolute bottom-3 right-3 bg-gray-900 rounded-full px-3 py-2 shadow-lg flex items-center gap-2 hover:bg-gray-800 transition-all active:scale-95"
          >
            <Maximize2 className="w-4 h-4 text-white" />
            <span className="text-xs font-semibold text-white">{expanded ? 'Volver a las fichas' : 'Expandir'}</span>
          </button>
        )}
      </div>
    </div>
  );
}
